import { StringContext } from "../../core/StringContext";
import { StringModule } from "../../core/StringModule";
import { StringSignalValue } from "../../core/managers/StringSignalHub";
import {
  StringSignalCondition,
  StringSignalEffect,
  StringSignalOperator,
  StringSignalRule,
} from "../../models/signal/StringSignalRule";
import { EventCallback } from "../../models/event/EventCallback";
import { StringObject } from "../../objects/StringObject";
import { parseSignalRules } from "../../utils/ParseSignalRules";
import { styleTxn } from "../../utils/style-txn";

type ConnectedSignalRule = {
  rule: StringSignalRule;
  callbacks: Array<{
    sourceId: string;
    signal: string;
    callback: EventCallback<StringSignalValue>;
  }>;
  previousState: boolean | null;
};

export class StringSignal extends StringModule {
  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "signal";
    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "signal", type: "string", fallback: "" },
    ];
  }

  onObjectConnected(object: StringObject): void {
    const expression = object.getProperty<string>("signal");
    if (!expression) return;

    const rules = parseSignalRules(expression);
    if (rules.length === 0) return;

    const connectedRules: ConnectedSignalRule[] = [];
    object.setProperty("signal-rules", connectedRules);

    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];
      const entry: ConnectedSignalRule = {
        rule,
        callbacks: [],
        previousState: null,
      };

      const callback: EventCallback<StringSignalValue> = () => {
        this.applyRuleState(object, entry);
      };
      for (const { sourceId, signal } of collectDependencies(rule)) {
        entry.callbacks.push({ sourceId, signal, callback });
        this.signals.subscribe(sourceId, signal, this.events, callback);
      }

      connectedRules.push(entry);
      this.applyRuleState(object, entry);
    }
  }

  onObjectDisconnected(object: StringObject): void {
    const connectedRules = object.getProperty<ConnectedSignalRule[]>("signal-rules");
    if (!connectedRules) return;

    for (let i = 0; i < connectedRules.length; i++) {
      const entry = connectedRules[i];
      for (let j = 0; j < entry.callbacks.length; j++) {
        const callbackEntry = entry.callbacks[j];
        this.signals.unsubscribe(
          callbackEntry.sourceId,
          callbackEntry.signal,
          this.events,
          callbackEntry.callback,
        );
      }

      if (entry.rule.effect.type === "class-toggle") {
        this.resetEffect(object, entry.rule.effect);
      }
    }
  }

  private applyRuleState(object: StringObject, entry: ConnectedSignalRule): void {
    const isActive = evaluateRule(entry.rule, this.signals);
    if (entry.previousState === isActive) return;
    entry.previousState = isActive;

    styleTxn.run(() => {
      this.applyEffectState(object, entry.rule.effect, isActive);
    });
  }

  private applyEffectState(
    object: StringObject,
    effect: StringSignalEffect,
    isActive: boolean,
  ): void {
    if (effect.type === "class-toggle") {
      isActive ? this.applyEffect(object, effect) : this.resetEffect(object, effect);
    } else if (isActive) {
      this.applyEffect(object, effect);
    }
  }

  private applyEffect(object: StringObject, effect: StringSignalEffect): void {
    this.applyToElementAndConnects(object, (element) => applyEffectToElement(element, effect));
  }

  private resetEffect(object: StringObject, effect: StringSignalEffect): void {
    this.applyToElementAndConnects(object, (element) => resetEffectOnElement(element, effect));
  }
}

function evaluateRule(
  rule: StringSignalRule,
  signals: { get(objectId: string, signal: string): StringSignalValue | undefined },
): boolean {
  for (let i = 0; i < rule.groups.length; i++) {
    const group = rule.groups[i];
    let groupActive = true;

    for (let j = 0; j < group.length; j++) {
      if (!evaluateCondition(group[j], signals)) {
        groupActive = false;
        break;
      }
    }

    if (groupActive) {
      return true;
    }
  }

  return false;
}

function evaluateCondition(
  condition: StringSignalCondition,
  signals: { get(objectId: string, signal: string): StringSignalValue | undefined },
): boolean {
  const value = signals.get(condition.sourceId, condition.signal);
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return false;
  }

  return compareSignalValue(value, condition.operator, condition.threshold);
}

function compareSignalValue(
  value: number,
  operator: StringSignalOperator,
  threshold: number,
): boolean {
  switch (operator) {
    case ">":
      return value > threshold;
    case ">=":
      return value >= threshold;
    case "<":
      return value < threshold;
    case "<=":
      return value <= threshold;
    case "==":
      return value === threshold;
    case "!=":
      return value !== threshold;
  }
}

function collectDependencies(
  rule: StringSignalRule,
): Array<{ sourceId: string; signal: string }> {
  const seen = new Set<string>();
  const result: Array<{ sourceId: string; signal: string }> = [];

  for (const group of rule.groups) {
    for (const { sourceId, signal } of group) {
      const key = `${sourceId}::${signal}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ sourceId, signal });
      }
    }
  }

  return result;
}

function applyEffectToElement(element: HTMLElement, effect: StringSignalEffect): void {
  switch (effect.type) {
    case "class-toggle":
    case "class-add":
      element.classList.add(effect.key);
      return;
    case "class-remove":
      element.classList.remove(effect.key);
      return;
    case "style-set":
      styleTxn.setProp(element, effect.key, effect.value);
      return;
    case "style-remove":
    case "var-remove":
      element.style.removeProperty(effect.key);
      return;
    case "var-set":
      styleTxn.setVar(element, effect.key, effect.value);
      return;
  }
}

function resetEffectOnElement(element: HTMLElement, effect: StringSignalEffect): void {
  switch (effect.type) {
    case "class-toggle":
      element.classList.remove(effect.key);
      return;
    default:
      return;
  }
}
